import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Badge, Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { getAllUsers } from "@modules/Users/services/userCrud";
import {
  MoistureIcon,
  ClipboardIcon,
  ClockIcon,
  WrenchIcon,
  AngleUpIcon,
  AngleDownIcon,
  ImageAddIconSmall,
} from "@components/icons";
import { Scissors } from "@styled-icons/bootstrap/Scissors";
import { FileText } from "@styled-icons/bootstrap/FileText";
import { CloudUpload } from "@styled-icons/bootstrap/CloudUpload";
import { REGELKONTROLLE_GROUPS } from "../constants/sichtkontrolle";

function makeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function toLocalDatetimeValue(value) {
  const d = value ? new Date(value) : new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

const IRRIGATION_TYPE = "Bewässerung";
const LEGACY_IRRIGATION_TYPE = "Gießmaßnahme";
const MEASURE_TYPES = [IRRIGATION_TYPE, "Schnittmaßnahme", "Regelkontrolle", "Freie Eintragung"];
const ADD_ENTRY_LABEL = "Eintrag hinzuf\u00fcgen";
const PRUNING_TYPE = MEASURE_TYPES[1];
const PRUNING_CATEGORIES = [
  { value: "Erziehungsschnitt", labelKey: "care_chat.pruning_formative" },
  { value: "Pflegeschnitt", labelKey: "care_chat.pruning_maintenance" },
  { value: "Kroneneinkuerzung / Kronenpflege", labelKey: "care_chat.pruning_crown_reduction" },
  { value: "Freistellen / Freischnitt", labelKey: "care_chat.pruning_clearing" },
  {
    value: "Sanierungsschnitt (Erhaltungsschnitt / Korrekturschnitt)",
    labelKey: "care_chat.pruning_remedial",
  },
  {
    value: "Sicherheitsschnitt (Verkehrssicherheitsschnitt)",
    labelKey: "care_chat.pruning_safety",
  },
];
const SENSOR_TASK_TYPES = ["Sensor am Baum montieren", "Sensorwartung am Baum durchfuehren"];
const TASK_MEASURE_TYPES = ["Regelkontrolle", ...SENSOR_TASK_TYPES];
const TASK_MEASURE_TYPE_OPTIONS = Array.from(new Set([...MEASURE_TYPES, ...SENSOR_TASK_TYPES]));

const THEME = {
  primary: "var(--color-action, #286CA5)",
  primaryDark: "var(--color-text-brand, #08424e)",
  primaryLight: "var(--color-action-subtle, #d4e6e7)",
  border: "var(--color-border-default, #e5e7eb)",
  borderLight: "var(--color-border-subtle, #f3f4f6)",
  background: "var(--color-bg-subtle, #fafafa)",
  text: "var(--color-text-primary, #495057)",
  muted: "var(--color-text-secondary, #6c757d)",
  danger: "#dc3545",
  chip: "var(--color-bg-subtle, #f8f9fa)",
  white: "var(--color-bg-surface, #ffffff)",
};

const COMPOSER_WRAP_STYLE = {
  zIndex: 3,
  borderTop: `2px solid ${THEME.border}`,
  background: THEME.white,
  borderTopLeftRadius: "12px",
  borderTopRightRadius: "12px",
  padding: "16px 16px 0",
  boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
};

const COMPOSER_PANEL_STYLE = {
  background: THEME.chip,
  border: `1px solid ${THEME.border}`,
  borderRadius: "12px",
  padding: "16px",
};

const INPUT_STYLE = {
  borderColor: THEME.border,
  borderRadius: "8px",
  background: THEME.white,
  color: THEME.text,
};

const TAG_BADGE_STYLE = {
  backgroundColor: THEME.primary,
  color: "#ffffff",
  border: `1px solid ${THEME.primary}`,
  borderRadius: "8px",
  padding: "0.2rem 0.6rem",
  fontWeight: 600,
};

const TAG_BUTTON_STYLE = {
  backgroundColor: THEME.white,
  color: THEME.text,
  border: `1px solid ${THEME.border}`,
  borderRadius: "6px",
  padding: "10px 12px",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const TASK_STATUSES = ["GEPLANT", "BEAUFTRAGT", "IN_ARBEIT"];
const HISTORY_STATUSES = ["ERLEDIGT", "NICHT_GESCHAFFT"];
const PRIORITY_OPTIONS = [
  { value: "NIEDRIG", labelKey: "care_chat.priority_low" },
  { value: "MITTEL", labelKey: "care_chat.priority_medium" },
  { value: "HOCH", labelKey: "care_chat.priority_high" },
  { value: "KRITISCH", labelKey: "care_chat.priority_critical" },
];
const WATER_AMOUNT_PRESETS = [100, 150, 200, 1000, 2000, 3000];

function getStatusOptionsForTimestamp(timestampLocal, now) {
  if (!timestampLocal) return [...TASK_STATUSES, ...HISTORY_STATUSES];
  const date = new Date(timestampLocal);
  return date > now ? TASK_STATUSES : HISTORY_STATUSES;
}

function getMeasureIcon(measureType) {
  switch (measureType) {
    case IRRIGATION_TYPE:
    case LEGACY_IRRIGATION_TYPE:
      return <MoistureIcon size="20px" color={THEME.primaryDark} />;
    case "Schnittmaßnahme":
      return <Scissors size={20} color={THEME.primaryDark} />;
    case "Regelkontrolle":
      return <ClipboardIcon size="20px" color={THEME.primaryDark} />;
    case "Sensor am Baum montieren":
    case "Sensorwartung am Baum durchfuehren":
      return <WrenchIcon size="20px" color={THEME.primaryDark} />;
    case "Freie Eintragung":
      return <FileText size={20} color={THEME.primaryDark} />;
    default:
      return <FileText size={20} color={THEME.primaryDark} />;
  }
}

function isIrrigationMeasureType(type) {
  return type === IRRIGATION_TYPE || type === LEGACY_IRRIGATION_TYPE;
}

const DEFAULT_INTERVAL_YEARS = 3;

function buildChecklistDraft() {
  const next = {};
  REGELKONTROLLE_GROUPS.forEach((group) => {
    group.items.forEach((item) => {
      next[item.id] = false;
    });
  });
  return next;
}

function getNextControlLocal(kontrolliertAmLocal, intervallJahre) {
  if (!kontrolliertAmLocal) return "";
  const base = new Date(kontrolliertAmLocal);
  if (Number.isNaN(base.getTime())) return "";
  const next = new Date(base);
  next.setFullYear(next.getFullYear() + Number(intervallJahre || 0));
  return toLocalDatetimeValue(next);
}

function toIsoFromLocal(localValue) {
  if (!localValue) return null;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function getNextControlIso(kontrolliertAmLocal, intervallJahre) {
  const iso = toIsoFromLocal(kontrolliertAmLocal);
  if (!iso) return null;
  const next = new Date(iso);
  next.setFullYear(next.getFullYear() + Number(intervallJahre || 0));
  return next.toISOString();
}

function getCareYear(plantingYear) {
  const year = Number(plantingYear);
  if (!year || Number.isNaN(year)) return "";
  const diff = new Date().getFullYear() - year + 1;
  if (diff <= 1) return "1";
  if (diff >= 3) return "3";
  return String(diff);
}

function RegelkontrolleChecklist({ checks, onToggle }) {
  const [openGroups, setOpenGroups] = useState({});
  return (
    <div className="d-grid gap-2">
      {REGELKONTROLLE_GROUPS.map((group) => {
        const checkedCount = group.items.filter((item) => checks?.[item.id]).length;
        const summaryCount = checkedCount ? ` (${checkedCount}/${group.items.length})` : "";
        return (
          <details
            key={group.id}
            open={Boolean(openGroups[group.id])}
            onToggle={(event) => {
              const isOpen = event?.currentTarget?.open;
              setOpenGroups((prev) => ({
                ...prev,
                [group.id]: typeof isOpen === "boolean" ? isOpen : !prev[group.id],
              }));
            }}
            className="regelkontrolle-checklist-item"
            style={{
              borderRadius: "8px",
              padding: "6px 10px",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontWeight: 600,
                listStyle: "none",
              }}
            >
              {group.label}
              {summaryCount}
            </summary>
            <div className="d-grid gap-1 mt-2">
              {group.items.map((item) => (
                <label key={item.id} className="d-flex align-items-start gap-2 small">
                  <input
                    type="checkbox"
                    checked={Boolean(checks?.[item.id])}
                    onChange={() => onToggle(item.id)}
                    className="tree-care-checkbox"
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
}

function buildDraft() {
  return {
    measureType: "",
    entryKind: "",
    timestampLocal: toLocalDatetimeValue(),
    beschreibung: "",
    grund: "",
    ausfuehrende: "",
    mengeLiter: "",
    methode: "",
    schnittArt: "",
    umfang: "",
    kontrollstufe: "",
    befund: "",
    empfehlung: "",
    status: "GEPLANT",
    priority: "MITTEL",
    assignedTo: "",
    assigneeId: null,
    costCenter: "",
    sichtkontrolleChecks: buildChecklistDraft(),
    kontrolliertVon: "Du",
    kontrolliertAmLocal: "",
    intervallJahre: DEFAULT_INTERVAL_YEARS,
    verkehrssicher: true,
    bemerkung: "",
    handlungsbedarf: null,
    abstimmungFach: false,
    eingehendeUntersuchung: false,
    baumpflegerischeMassnahmen: "",
    kontrollintervallKuenftig: "",
    kontrollintervallWieBisher: false,
    faellung: false,
    erledigungSofort: false,
    erledigungWochen: "",
    anmerkungen: "",
    regelkontrolleMode: "form", // "form" or "upload"
    regelkontrolleDocument: null, // { name, size, type, file }
    freieEintragungDocument: null, // { name, size, type, file }
    irrigationProject: "",
    irrigationTreeSpecies: "",
    irrigationCareYear: "",
    irrigationWeather: "",
    irrigationSoilMoisture: "",
    irrigationTreeCondition: "",
    irrigationNotes: "",
    pruningType: "",
    pruningTreeCondition: "",
    pruningNotes: "",
  };
}

function todayDateInputValue() {
  return toLocalDatetimeValue().slice(0, 10);
}

function toLocalDateInputString(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function shiftDateInputValue(baseDateInput, daysToAdd = 0) {
  if (!baseDateInput) return "";
  const [year, month, day] = baseDateInput.split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + daysToAdd);
  return toLocalDateInputString(date);
}

export default function TreeDetailCareComposerInline({
  onSave,
  prefill,
  onPrefillApplied,
  initialStep = null,
  mode,
  tree,
  onAddImage,
  collapsed = false,
  onToggleCollapsed,
  futureOnly = false,
  collapsedLabel,
  hideCollapseHeader = false,
  defaultIrrigationLiters = null,
  defaultIrrigationAssignee = null,
}) {
  const [step, setStep] = useState(0);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [draft, setDraft] = useState(buildDraft());
  const { t } = useTranslation();
  const { mobile } = useSelector((state) => state.mobile);
  const [showAssigneeError, setShowAssigneeError] = useState(false);
  const [showPruningError, setShowPruningError] = useState(false);
  const [showFutureError, setShowFutureError] = useState(false);
  const [showWaterAmountError, setShowWaterAmountError] = useState(false);

  const MEASURE_TYPE_LABELS = {
    [IRRIGATION_TYPE]: t("care_chat.measure_irrigation"),
    [LEGACY_IRRIGATION_TYPE]: t("care_chat.measure_irrigation"),
    Schnittmaßnahme: t("care_chat.measure_pruning"),
    Regelkontrolle: t("care_chat.measure_inspection"),
    "Freie Eintragung": t("care_chat.measure_free"),
    "Sensor am Baum montieren": t("care_chat.sensor_install_task"),
    "Sensorwartung am Baum durchfuehren": t("care_chat.sensor_maintenance_task"),
  };

  const { data: usersData } = useQuery("care-users", () => getAllUsers({ pageSize: 1000 }), {
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  const users =
    usersData?.results || usersData?.data || (Array.isArray(usersData) ? usersData : []);
  const isTaskMode = mode === "tasks";
  const useChatChrome = true;
  const isTaskMeasureType = (type) => TASK_MEASURE_TYPES.includes(type);
  const isIrrigation = isIrrigationMeasureType(draft.measureType);
  const isPruning = draft.measureType === PRUNING_TYPE;
  const isFreeEntry = draft.measureType === "Freie Eintragung";
  const resolvedEntryKind =
    draft.entryKind || (isTaskMode || isTaskMeasureType(draft.measureType) ? "task" : "care");
  const isTaskEntry = resolvedEntryKind === "task";
  const isRegelkontrolle = draft.measureType === "Regelkontrolle";
  const isPastRegelkontrolle = isRegelkontrolle && new Date(draft.timestampLocal) <= new Date();
  const canFillChecklist = isRegelkontrolle && isPastRegelkontrolle;
  const isPastPruning = isPruning && new Date(draft.timestampLocal) <= new Date();
  const canFillPruning = isPruning && isPastPruning;
  const canFillDetails = canFillChecklist || canFillPruning;
  const detailFillLabel = canFillChecklist
    ? t("care_chat.fill_checklist")
    : t("care_chat.fill_protocol");
  const isAssigneeMissing = !draft.assigneeId;
  const nextControlLocal = getNextControlLocal(draft.kontrolliertAmLocal, draft.intervallJahre);
  const nextControlDisplay = nextControlLocal ? nextControlLocal.replace("T", " ") : "";
  const todayMinDate = todayDateInputValue();

  useEffect(() => {
    if (!prefill) return;
    const forceTaskEntry = isTaskMeasureType(prefill.measureType);
    const prefillData = prefill.data || {};
    const prefillIrrigationLog = prefillData.irrigationLog || {};
    const prefillPruningLog = prefillData.pruningLog || {};
    const prefillChecklistMeta = prefillData.sichtkontrolleMeta || {};
    const prefillAssigneeId =
      prefill.assigneeId ||
      users.find((u) => {
        const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
        return (
          (prefill.assignedTo && fullName === prefill.assignedTo) ||
          (prefill.assignedTo && u.username === prefill.assignedTo) ||
          (prefill.assignedTo && u.email === prefill.assignedTo)
        );
      })?.id ||
      null;
    const prefillTimestampLocal = prefill.timestamp
      ? toLocalDatetimeValue(prefill.timestamp)
      : undefined;
    setDraft((prev) => ({
      ...prev,
      measureType: prefill.measureType ?? prev.measureType,
      status: prefill.status ?? prev.status,
      priority: prefill.priority ?? prev.priority,
      entryKind: isTaskMode || forceTaskEntry ? "task" : prev.entryKind,
      timestampLocal: prefillTimestampLocal ?? prev.timestampLocal,
      beschreibung: prefill.beschreibung ?? prev.beschreibung,
      assignedTo: prefill.assignedTo ?? prev.assignedTo,
      assigneeId: prefillAssigneeId ?? prev.assigneeId,
      costCenter: prefill.costCenter ?? prev.costCenter,
      grund: prefillData.grund ?? prev.grund,
      ausfuehrende: prefillData.ausfuehrende ?? prev.ausfuehrende,
      mengeLiter:
        prefillData.mengeLiter ??
        prefillIrrigationLog.waterLiters ??
        prefillData.waterLiters ??
        prev.mengeLiter,
      methode: prefillData.methode ?? prefillIrrigationLog.method ?? prev.methode,
      irrigationWeather: prefillIrrigationLog.weather ?? prev.irrigationWeather,
      irrigationSoilMoisture: prefillIrrigationLog.soilMoisture ?? prev.irrigationSoilMoisture,
      irrigationTreeCondition: prefillIrrigationLog.treeCondition ?? prev.irrigationTreeCondition,
      irrigationNotes: prefillIrrigationLog.notes ?? prev.irrigationNotes,
      pruningType:
        prefillPruningLog.type ??
        prefillData.schnittArt ??
        prefillData.pruningType ??
        prev.pruningType,
      pruningTreeCondition:
        prefillPruningLog.treeCondition ?? prefillData.treeCondition ?? prev.pruningTreeCondition,
      pruningNotes: prefillPruningLog.notes ?? prev.pruningNotes,
      sichtkontrolleChecks:
        prefillData.sichtkontrolleChecks ??
        prefill.sichtkontrolleChecks ??
        prev.sichtkontrolleChecks,
      kontrolliertVon:
        prefillChecklistMeta.kontrolliertVon ?? prefill.assignedTo ?? prev.kontrolliertVon,
      kontrolliertAmLocal: prefillChecklistMeta.kontrolliertAm
        ? toLocalDatetimeValue(prefillChecklistMeta.kontrolliertAm)
        : prefillTimestampLocal ?? prev.kontrolliertAmLocal,
      intervallJahre: prefillChecklistMeta.intervallJahre ?? prev.intervallJahre,
      verkehrssicher:
        typeof prefillChecklistMeta.verkehrssicher === "boolean"
          ? prefillChecklistMeta.verkehrssicher
          : prev.verkehrssicher,
      bemerkung: prefillChecklistMeta.bemerkung ?? prev.bemerkung,
    }));
    const resolvedInitialStep = Number.isFinite(initialStep) ? initialStep : 2;
    setStep(resolvedInitialStep);
    onPrefillApplied?.();
  }, [prefill, onPrefillApplied, isTaskMode, users, initialStep]);

  useEffect(() => {
    if (!tree) return;
    setDraft((prev) => {
      const next = { ...prev };
      if (!next.irrigationProject) {
        const projectName = tree?.area?.project?.name;
        const areaName = tree?.area?.name;
        const location = [projectName, areaName].filter(Boolean).join(" / ");
        if (location) next.irrigationProject = location;
      }
      if (!next.irrigationTreeSpecies) {
        const species =
          tree?.species?.name || tree?.species?.de_name || tree?.species || tree?.name || "";
        if (species) next.irrigationTreeSpecies = species;
      }
      if (!next.irrigationCareYear && tree?.planting_year) {
        next.irrigationCareYear = getCareYear(tree.planting_year);
      }
      return next;
    });
  }, [
    tree?.id,
    tree?.planting_year,
    tree?.name,
    tree?.species,
    tree?.species?.name,
    tree?.species?.de_name,
    tree?.area?.name,
    tree?.area?.project?.name,
  ]);

  useEffect(() => {
    if (!isTaskMode || !isIrrigation) return;
    if (draft.mengeLiter !== "" && draft.mengeLiter !== null && draft.mengeLiter !== undefined)
      return;
    if (!Number.isFinite(Number(defaultIrrigationLiters)) || Number(defaultIrrigationLiters) <= 0)
      return;
    setDraft((prev) => {
      if (prev.mengeLiter !== "" && prev.mengeLiter !== null && prev.mengeLiter !== undefined) {
        return prev;
      }
      return { ...prev, mengeLiter: String(defaultIrrigationLiters) };
    });
  }, [isTaskMode, isIrrigation, defaultIrrigationLiters, draft.mengeLiter]);

  useEffect(() => {
    if (!isTaskMode || !isIrrigation) return;
    if (draft.assigneeId || draft.assignedTo) return;
    if (!defaultIrrigationAssignee?.assigneeId) return;
    setDraft((prev) => {
      if (prev.assigneeId || prev.assignedTo) return prev;
      return {
        ...prev,
        assigneeId: defaultIrrigationAssignee.assigneeId,
        assignedTo: defaultIrrigationAssignee.assignedTo || "",
      };
    });
  }, [isTaskMode, isIrrigation, draft.assigneeId, draft.assignedTo, defaultIrrigationAssignee]);

  function resetDraft() {
    setDraft(buildDraft());
    setStep(0);
    setShowAssigneeError(false);
    setShowPruningError(false);
    setShowFutureError(false);
    setShowWaterAmountError(false);
  }

  function handleSave() {
    if (isAssigneeMissing) {
      setShowAssigneeError(true);
      return;
    }
    const now = new Date();
    const measureType = draft.measureType || "Freie Eintragung";
    const timestamp = draft.timestampLocal
      ? new Date(draft.timestampLocal).toISOString()
      : now.toISOString();
    if (futureOnly && new Date(timestamp) <= now) {
      setShowFutureError(true);
      return;
    }
    if (isIrrigation && !String(draft.mengeLiter ?? "").trim()) {
      setShowWaterAmountError(true);
      return;
    }
    const isPast = new Date(timestamp) <= now;
    const entryKind = isTaskMeasureType(measureType)
      ? "task"
      : draft.entryKind || (isTaskMode ? "task" : "care");
    let status = draft.status || "GEPLANT";
    // Auto-set status to ERLEDIGT for care entries in the past
    // For task entries, user can manually select status via dropdown
    if (entryKind === "care" && isPast && ["GEPLANT", "BEAUFTRAGT", "IN_ARBEIT"].includes(status)) {
      status = "ERLEDIGT";
    }
    const mengeLiterValue = draft.mengeLiter === "" ? undefined : draft.mengeLiter;
    const kontrolliertAmIso = toIsoFromLocal(draft.kontrolliertAmLocal);
    const naechsteKontrolleIso = getNextControlIso(draft.kontrolliertAmLocal, draft.intervallJahre);
    const hasRegelkontrolleMeta = measureType === "Regelkontrolle" && kontrolliertAmIso;
    const resolvedAssignedTo =
      draft.assignedTo || (measureType === "Regelkontrolle" ? draft.kontrolliertVon : "");
    const shouldIncludeIrrigationLog = isIrrigationMeasureType(measureType);
    const shouldIncludePruningLog = measureType === PRUNING_TYPE && step === 3;
    if (
      shouldIncludePruningLog &&
      (!draft.pruningType.trim() || !draft.pruningTreeCondition.trim())
    ) {
      setShowPruningError(true);
      return;
    }
    const irrigationLog = shouldIncludeIrrigationLog
      ? {
          projectLocation: draft.irrigationProject || "",
          treeSpecies: draft.irrigationTreeSpecies || "",
          careYear: draft.irrigationCareYear || "",
          date: draft.timestampLocal.slice(0, 10),
          time: draft.timestampLocal.slice(11, 16),
          waterLiters: mengeLiterValue ?? "",
          method: draft.methode || "",
          reason: draft.grund || "",
          weather: draft.irrigationWeather || "",
          soilMoisture: draft.irrigationSoilMoisture || "",
          treeCondition: draft.irrigationTreeCondition || "",
          responsiblePerson: resolvedAssignedTo || "",
          notes: draft.irrigationNotes || "",
        }
      : null;
    const pruningLog = shouldIncludePruningLog
      ? {
          type: draft.pruningType || "",
          treeCondition: draft.pruningTreeCondition || "",
          notes: draft.pruningNotes || "",
          responsiblePerson: resolvedAssignedTo || "",
          date: draft.timestampLocal.slice(0, 10),
          time: draft.timestampLocal.slice(11, 16),
        }
      : null;
    const entry = {
      ...(entryKind === "care" ? { id: makeId() } : {}),
      entryKind,
      source: entryKind === "task" ? "task" : undefined,
      measureType,
      timestamp,
      future: new Date(timestamp) > now,
      status,
      priority: draft.priority || "MITTEL",
      assignedTo: resolvedAssignedTo || "",
      assigneeId: draft.assigneeId || undefined,
      costCenter: draft.costCenter || "",
      tags: [],
      attachments: [],
      author: "Du",
      data: {
        beschreibung: draft.beschreibung || "",
        grund: draft.grund || "",
        ausfuehrende: draft.ausfuehrende || "",
        frist: "",
        mengeLiter: mengeLiterValue,
        methode: draft.methode || "",
        schnittArt: draft.schnittArt || "",
        umfang: draft.umfang || "",
        kontrollstufe: draft.kontrollstufe || "",
        befund: draft.befund || "",
        empfehlung: draft.empfehlung || "",
        ...(irrigationLog ? { irrigationLog } : {}),
        ...(pruningLog ? { pruningLog } : {}),
        ...(hasRegelkontrolleMeta
          ? {
              ...(draft.regelkontrolleMode === "form"
                ? { sichtkontrolleChecks: draft.sichtkontrolleChecks }
                : {}),
              ...(draft.regelkontrolleDocument
                ? {
                    regelkontrolleDocument: {
                      name: draft.regelkontrolleDocument.name,
                      size: draft.regelkontrolleDocument.size,
                      type: draft.regelkontrolleDocument.type,
                      file: draft.regelkontrolleDocument.file,
                    },
                  }
                : {}),
              sichtkontrolleMeta: {
                kontrolliertVon: draft.kontrolliertVon || "",
                kontrolliertAm: kontrolliertAmIso,
                naechsteKontrolle: naechsteKontrolleIso,
                intervallJahre: Number(draft.intervallJahre || 0),
                verkehrssicher: Boolean(draft.verkehrssicher),
                bemerkung: draft.bemerkung || "",
                regelkontrolleMode: draft.regelkontrolleMode,
                ...(draft.regelkontrolleMode === "form"
                  ? {
                      weiteresVorgehen: {
                        handlungsbedarf: draft.handlungsbedarf,
                        abstimmungFach: Boolean(draft.abstimmungFach),
                        eingehendeUntersuchung: Boolean(draft.eingehendeUntersuchung),
                        baumpflegerischeMassnahmen: draft.baumpflegerischeMassnahmen || "",
                        kontrollintervallKuenftig: draft.kontrollintervallKuenftig || "",
                        kontrollintervallWieBisher: Boolean(draft.kontrollintervallWieBisher),
                        faellung: Boolean(draft.faellung),
                        erledigungSofort: Boolean(draft.erledigungSofort),
                        erledigungWochen: draft.erledigungWochen || "",
                        anmerkungen: draft.anmerkungen || "",
                      },
                    }
                  : {}),
              },
            }
          : {}),
        ...(isFreeEntry && draft.freieEintragungDocument
          ? {
              freieEintragungDocument: {
                name: draft.freieEintragungDocument.name,
                size: draft.freieEintragungDocument.size,
                type: draft.freieEintragungDocument.type,
                file: draft.freieEintragungDocument.file,
              },
            }
          : {}),
      },
    };

    onSave(entry);
    resetDraft();
  }

  const canToggleCollapse = !hideCollapseHeader && typeof onToggleCollapsed === "function";
  const isCollapsed = canToggleCollapse ? collapsed : false;

  const panelWrapperStyle = {
    maxHeight: isCollapsed ? "0px" : isTaskMode ? "100%" : "60vh",
    opacity: isCollapsed ? 0 : 1,
    transform: isCollapsed ? "translateY(8px)" : "translateY(0)",
    transition: "max-height 260ms ease, opacity 200ms ease, transform 260ms ease",
    overflow: isCollapsed ? "hidden" : "auto",
    pointerEvents: isCollapsed ? "none" : "auto",
    minHeight: isTaskMode ? "100%" : undefined,
  };

  return (
    <div
      style={{
        ...COMPOSER_WRAP_STYLE,
        borderTop: useChatChrome ? COMPOSER_WRAP_STYLE.borderTop : "0",
        borderTopLeftRadius: useChatChrome ? COMPOSER_WRAP_STYLE.borderTopLeftRadius : "8px",
        borderTopRightRadius: useChatChrome ? COMPOSER_WRAP_STYLE.borderTopRightRadius : "8px",
        boxShadow: useChatChrome ? COMPOSER_WRAP_STYLE.boxShadow : "none",
        padding: useChatChrome ? COMPOSER_WRAP_STYLE.padding : "0",
        width: mobile && useChatChrome ? "calc(100% + 32px)" : "100%",
        marginLeft: mobile && useChatChrome ? "-16px" : 0,
      }}
    >
      {canToggleCollapse && (
        <button
          type="button"
          onClick={() => onToggleCollapsed?.()}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: THEME.primary,
            border: `1px solid ${THEME.primary}`,
            borderRadius: "10px",
            padding: "10px 12px",
            fontWeight: 600,
            color: "#ffffff",
            boxShadow: "0 4px 10px rgba(13, 110, 253, 0.28)",
            marginBottom: "10px",
          }}
        >
          <span>{collapsedLabel || t("care_chat.add_entry")}</span>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {isCollapsed ? <AngleUpIcon size="1em" /> : <AngleDownIcon size="1em" />}
          </span>
        </button>
      )}
      <div style={panelWrapperStyle}>
        <div className="d-flex flex-column gap-3" style={COMPOSER_PANEL_STYLE}>
          {onAddImage && (
            <div className="d-flex justify-content-end">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={onAddImage}
                style={{ borderColor: THEME.primary, color: THEME.primary }}
              >
                <ImageAddIconSmall /> {t("care_chat.add_photo")}
              </Button>
            </div>
          )}

          {draft.measureType && (
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Badge style={TAG_BADGE_STYLE}>
                {MEASURE_TYPE_LABELS[draft.measureType] || draft.measureType}
              </Badge>
            </div>
          )}

          {step === 0 && (
            <div className="d-grid gap-2">
              <div
                className="gap-2"
                style={{
                  display: "grid",
                  gridTemplateColumns: mobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                }}
              >
                {MEASURE_TYPES.map((type) => (
                  <Button
                    key={type}
                    variant="light"
                    style={{ ...TAG_BUTTON_STYLE, fontSize: "0.95rem" }}
                    onClick={() => {
                      const nextEntryKind = isTaskMode || isTaskMeasureType(type) ? "task" : "care";
                      setDraft((prev) => ({
                        ...prev,
                        measureType: type,
                        entryKind: nextEntryKind,
                        selectedTemplateTags: [],
                        customTags: "",
                      }));
                      setStep(2);
                    }}
                  >
                    {getMeasureIcon(type)}
                    <span>{MEASURE_TYPE_LABELS[type] || type}</span>
                  </Button>
                ))}
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setShowMoreOptions((prev) => !prev)}
                style={{
                  borderColor: THEME.border,
                  color: THEME.text,
                  borderRadius: "8px",
                  padding: "0.4rem 0.8rem",
                  backgroundColor: THEME.white,
                }}
              >
                {showMoreOptions ? t("care_chat.show_less") : t("care_chat.show_more")}
              </Button>
              {showMoreOptions && (
                <div
                  className="gap-2"
                  style={{
                    display: "grid",
                    gridTemplateColumns: mobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  }}
                >
                  {SENSOR_TASK_TYPES.map((type) => (
                    <Button
                      key={type}
                      variant="light"
                      style={{ ...TAG_BUTTON_STYLE, fontSize: "0.95rem" }}
                      onClick={() => {
                        setDraft((prev) => ({
                          ...prev,
                          measureType: type,
                          entryKind: "task",
                          selectedTemplateTags: [],
                          customTags: "",
                        }));
                        setStep(2);
                      }}
                    >
                      {getMeasureIcon(type)}
                      <span>{MEASURE_TYPE_LABELS[type] || type}</span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div
              className="border rounded-3 p-3"
              style={{ background: THEME.white, borderColor: THEME.border, borderRadius: "8px" }}
            >
              <div className="d-grid gap-2">
                {isTaskMode && (
                  <div className="d-flex flex-column gap-1">
                    <div className="small fw-semibold" style={{ color: THEME.text }}>
                      {t("task_filter_measure_type", "Aufgabentyp")}
                    </div>
                    <Form.Select
                      value={draft.measureType || ""}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setDraft((prev) => ({
                          ...prev,
                          measureType: nextType,
                          entryKind: "task",
                        }));
                      }}
                      style={INPUT_STYLE}
                    >
                      {TASK_MEASURE_TYPE_OPTIONS.map((type) => (
                        <option key={type} value={type}>
                          {MEASURE_TYPE_LABELS[type] || type}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                )}
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="small fw-semibold" style={{ color: THEME.text }}>
                    {t("care_chat.datetime")}
                  </div>
                </div>
                <div
                  className="gap-2"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  }}
                >
                  {isTaskMode && (
                    <div
                      className="d-grid gap-1"
                      style={{
                        gridColumn: "1 / -1",
                        gridTemplateColumns: mobile
                          ? "repeat(2, minmax(0, 1fr))"
                          : "repeat(4, minmax(0, 1fr))",
                      }}
                    >
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          const baseDate = todayDateInputValue();
                          const nextTimestamp = baseDate + draft.timestampLocal.slice(10);
                          setDraft((prev) => ({ ...prev, timestampLocal: nextTimestamp }));
                          if (showFutureError) setShowFutureError(false);
                        }}
                        style={{ borderRadius: "6px", padding: "0.2rem 0.5rem", width: "100%" }}
                      >
                        {t("care_chat.today")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          const baseDate = shiftDateInputValue(todayDateInputValue(), 1);
                          const nextTimestamp = baseDate + draft.timestampLocal.slice(10);
                          setDraft((prev) => ({ ...prev, timestampLocal: nextTimestamp }));
                          if (showFutureError) setShowFutureError(false);
                        }}
                        style={{ borderRadius: "6px", padding: "0.2rem 0.5rem", width: "100%" }}
                      >
                        {t("care_chat.tomorrow")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          const baseDate = shiftDateInputValue(todayDateInputValue(), 7);
                          const nextTimestamp = baseDate + draft.timestampLocal.slice(10);
                          setDraft((prev) => ({ ...prev, timestampLocal: nextTimestamp }));
                          if (showFutureError) setShowFutureError(false);
                        }}
                        style={{ borderRadius: "6px", padding: "0.2rem 0.5rem", width: "100%" }}
                      >
                        {t("care_chat.in_one_week")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          const todayDate = todayDateInputValue();
                          const [y, m, d] = todayDate.split("-").map(Number);
                          const nextDate = new Date(y, m - 1, d);
                          nextDate.setMonth(nextDate.getMonth() + 1);
                          const baseDate = toLocalDateInputString(nextDate);
                          const nextTimestamp = baseDate + draft.timestampLocal.slice(10);
                          setDraft((prev) => ({ ...prev, timestampLocal: nextTimestamp }));
                          if (showFutureError) setShowFutureError(false);
                        }}
                        style={{ borderRadius: "6px", padding: "0.2rem 0.5rem", width: "100%" }}
                      >
                        {t("care_chat.in_one_month")}
                      </Button>
                    </div>
                  )}
                  <div className="d-flex flex-column gap-1">
                    {!isTaskMode && (
                      <div className="small" style={{ color: THEME.muted }}>
                        {t("care_chat.date")}
                      </div>
                    )}
                    <Form.Control
                      type="date"
                      value={draft.timestampLocal.slice(0, 10)}
                      min={futureOnly ? todayMinDate : undefined}
                      onChange={(e) => {
                        const nextTimestamp = e.target.value + draft.timestampLocal.slice(10);
                        setDraft((prev) => {
                          const options = futureOnly
                            ? TASK_STATUSES
                            : getStatusOptionsForTimestamp(nextTimestamp, new Date());
                          const nextStatus = options.includes(prev.status)
                            ? prev.status
                            : options[0];
                          return { ...prev, timestampLocal: nextTimestamp, status: nextStatus };
                        });
                        if (showFutureError) setShowFutureError(false);
                      }}
                      style={INPUT_STYLE}
                    />
                  </div>
                  <div className="d-flex flex-column gap-1">
                    {!isTaskMode && (
                      <div className="small" style={{ color: THEME.muted }}>
                        {t("care_chat.time")}
                      </div>
                    )}
                    <div className="position-relative">
                      <Form.Control
                        type="time"
                        value={draft.timestampLocal.slice(11, 16)}
                        onChange={(e) => {
                          const nextTimestamp = draft.timestampLocal.slice(0, 11) + e.target.value;
                          setDraft((prev) => {
                            const options = futureOnly
                              ? TASK_STATUSES
                              : getStatusOptionsForTimestamp(nextTimestamp, new Date());
                            const nextStatus = options.includes(prev.status)
                              ? prev.status
                              : options[0];
                            return { ...prev, timestampLocal: nextTimestamp, status: nextStatus };
                          });
                          if (showFutureError) setShowFutureError(false);
                        }}
                        style={{ ...INPUT_STYLE, paddingRight: "36px" }}
                      />
                      <ClockIcon
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: THEME.muted,
                          width: "16px",
                          pointerEvents: "none",
                          left: "auto",
                        }}
                      />
                    </div>
                  </div>
                </div>
                {futureOnly && showFutureError && (
                  <div className="small" style={{ color: THEME.danger }}>
                    {t("care_chat.future_date_required")}
                  </div>
                )}
                <div className="d-flex flex-column gap-1">
                  <div className="small" style={{ color: THEME.muted }}>
                    {t("care_chat.responsible_person")}{" "}
                    <span style={{ color: THEME.danger }}>*</span>
                  </div>
                  <Form.Select
                    value={draft.assigneeId || ""}
                    onChange={(e) => {
                      const selected = users.find((u) => String(u.id) === e.target.value);
                      const name = selected
                        ? [selected.first_name, selected.last_name].filter(Boolean).join(" ") ||
                          selected.username ||
                          selected.email ||
                          ""
                        : "";
                      setDraft((prev) => ({
                        ...prev,
                        assigneeId: selected ? selected.id : null,
                        assignedTo: name,
                      }));
                      if (showAssigneeError) setShowAssigneeError(false);
                    }}
                    style={INPUT_STYLE}
                    isInvalid={showAssigneeError && isAssigneeMissing}
                  >
                    <option value="">{t("care_chat.select_placeholder")}</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {[u.first_name, u.last_name].filter(Boolean).join(" ") ||
                          u.username ||
                          u.email}
                      </option>
                    ))}
                  </Form.Select>
                  {showAssigneeError && isAssigneeMissing && (
                    <div className="small" style={{ color: THEME.danger }}>
                      {t("care_chat.please_select_person")}
                    </div>
                  )}
                </div>
                {isTaskEntry && (
                  <div className="d-flex flex-column gap-1">
                    <div className="small" style={{ color: THEME.muted }}>
                      {t("status")}
                    </div>
                    <Form.Select
                      value={draft.status || "GEPLANT"}
                      onChange={(e) => {
                        setDraft((prev) => ({ ...prev, status: e.target.value }));
                      }}
                      style={INPUT_STYLE}
                    >
                      {[...TASK_STATUSES, ...HISTORY_STATUSES].map((status) => (
                        <option key={status} value={status}>
                          {t(`status_${status}`)}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                )}
                <div className="d-flex flex-column gap-1">
                  <div className="small" style={{ color: THEME.muted }}>
                    {t("care_chat.short_description_optional")}
                  </div>
                  <Form.Control
                    placeholder={t("care_chat.short_description_placeholder")}
                    value={draft.beschreibung}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, beschreibung: e.target.value }))
                    }
                    style={INPUT_STYLE}
                  />
                </div>
                {isFreeEntry && (
                  <div className="d-grid gap-2 mt-1">
                    <div className="small fw-semibold" style={{ color: THEME.text }}>
                      {t("care_chat.upload_document")}
                    </div>
                    <div
                      className="d-flex flex-column align-items-center justify-content-center gap-2"
                      style={{
                        border: `2px dashed ${
                          draft.freieEintragungDocument ? THEME.primary : THEME.border
                        }`,
                        borderRadius: "8px",
                        padding: "1rem",
                        cursor: "pointer",
                        background: draft.freieEintragungDocument
                          ? "var(--color-success-subtle, #f0f7f0)"
                          : THEME.background,
                        position: "relative",
                      }}
                      onClick={() =>
                        document.getElementById("freie-eintragung-file-input")?.click()
                      }
                    >
                      <input
                        id="freie-eintragung-file-input"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setDraft((prev) => ({
                            ...prev,
                            freieEintragungDocument: {
                              name: file.name,
                              size: file.size,
                              type: file.type,
                              file,
                            },
                          }));
                          e.target.value = "";
                        }}
                      />
                      {draft.freieEintragungDocument ? (
                        <>
                          <FileText size={24} style={{ color: THEME.primary }} />
                          <div className="small fw-semibold" style={{ color: THEME.text }}>
                            {draft.freieEintragungDocument.name}
                          </div>
                          <div className="small" style={{ color: THEME.muted }}>
                            {(draft.freieEintragungDocument.size / 1024).toFixed(0)} KB
                          </div>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDraft((prev) => ({ ...prev, freieEintragungDocument: null }));
                            }}
                            style={{
                              borderRadius: "6px",
                              padding: "0.2rem 0.5rem",
                              fontSize: "0.75rem",
                            }}
                          >
                            {t("care_chat.remove")}
                          </Button>
                        </>
                      ) : (
                        <>
                          <CloudUpload size={28} style={{ color: THEME.muted }} />
                          <div className="small" style={{ color: THEME.muted }}>
                            {t("care_chat.upload_document_hint")}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div className="d-flex flex-column gap-1">
                  <div className="small" style={{ color: THEME.muted }}>
                    {t("care_chat.priority")}
                  </div>
                  <Form.Select
                    value={draft.priority}
                    onChange={(e) => setDraft((prev) => ({ ...prev, priority: e.target.value }))}
                    style={INPUT_STYLE}
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                {isIrrigation && (
                  <div className="d-flex flex-column gap-1">
                    <div className="small" style={{ color: THEME.muted }}>
                      {t("care_chat.water_amount_liters")}{" "}
                      <span style={{ color: THEME.danger }}>*</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mb-1">
                      {WATER_AMOUNT_PRESETS.map((amount) => {
                        const isActive = String(draft.mengeLiter ?? "") === String(amount);
                        return (
                          <Button
                            key={amount}
                            size="sm"
                            variant={isActive ? "primary" : "outline-secondary"}
                            onClick={() => {
                              setDraft((prev) => ({ ...prev, mengeLiter: String(amount) }));
                              if (showWaterAmountError) setShowWaterAmountError(false);
                            }}
                            style={{
                              borderRadius: "6px",
                              padding: "0.2rem 0.6rem",
                              fontSize: "0.78rem",
                            }}
                          >
                            {amount} l
                          </Button>
                        );
                      })}
                    </div>
                    <Form.Control
                      type="number"
                      value={draft.mengeLiter}
                      onChange={(e) => {
                        setDraft((prev) => ({ ...prev, mengeLiter: e.target.value }));
                        if (showWaterAmountError) setShowWaterAmountError(false);
                      }}
                      style={INPUT_STYLE}
                      isInvalid={showWaterAmountError && !String(draft.mengeLiter ?? "").trim()}
                    />
                    {showWaterAmountError && !String(draft.mengeLiter ?? "").trim() && (
                      <div className="small" style={{ color: THEME.danger }}>
                        {t("care_chat.water_amount_required")}
                      </div>
                    )}
                  </div>
                )}
                <div className="d-flex flex-wrap gap-2">
                  {!isTaskMode && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setStep(0)}
                      style={{
                        borderColor: THEME.primary,
                        color: THEME.primary,
                        borderRadius: "6px",
                        padding: "0.5rem 1rem",
                        backgroundColor: "transparent",
                      }}
                    >
                      {t("care_chat.back")}
                    </Button>
                  )}
                  {canFillDetails ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        if (isAssigneeMissing) {
                          setShowAssigneeError(true);
                          return;
                        }
                        if (canFillChecklist) {
                          setDraft((prev) => ({
                            ...prev,
                            kontrolliertAmLocal: prev.kontrolliertAmLocal || prev.timestampLocal,
                            kontrolliertVon: prev.kontrolliertVon || prev.assignedTo || "Du",
                            sichtkontrolleChecks:
                              prev.sichtkontrolleChecks || buildChecklistDraft(),
                          }));
                          setStep(3);
                          return;
                        }
                        setShowPruningError(false);
                        setStep(3);
                      }}
                      style={{
                        background: THEME.primary,
                        borderColor: THEME.primary,
                        borderRadius: "6px",
                        padding: "0.5rem 1rem",
                        color: "#ffffff",
                      }}
                    >
                      {detailFillLabel}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleSave}
                      style={{
                        background: THEME.primary,
                        borderColor: THEME.primary,
                        borderRadius: "6px",
                        padding: "0.5rem 1rem",
                        color: "#ffffff",
                      }}
                    >
                      {t("care_chat.save")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 3 && isRegelkontrolle && (
            <div
              className="border rounded-3 p-3 regelkontrolle-checklist-container"
              style={{ background: THEME.white, borderColor: THEME.border, borderRadius: "8px" }}
            >
              <div className="d-grid gap-2">
                <div className="small fw-semibold" style={{ color: THEME.text }}>
                  {t("care_chat.inspection_title")}
                </div>

                {/* Mode toggle: form vs upload */}
                <div className="d-flex gap-2" style={{ flexWrap: mobile ? "wrap" : "nowrap" }}>
                  <Button
                    size="sm"
                    variant={draft.regelkontrolleMode === "form" ? "primary" : "outline-secondary"}
                    onClick={() => setDraft((prev) => ({ ...prev, regelkontrolleMode: "form" }))}
                    style={{
                      ...(draft.regelkontrolleMode === "form"
                        ? { background: THEME.primary, borderColor: THEME.primary, color: "#fff" }
                        : { borderColor: THEME.border, color: THEME.text }),
                      borderRadius: "6px",
                      padding: "0.35rem 0.75rem",
                    }}
                  >
                    <FileText size={14} style={{ marginRight: 4 }} />
                    {t("care_chat.fill_form")}
                  </Button>
                  <Button
                    size="sm"
                    variant={
                      draft.regelkontrolleMode === "upload" ? "primary" : "outline-secondary"
                    }
                    onClick={() => setDraft((prev) => ({ ...prev, regelkontrolleMode: "upload" }))}
                    style={{
                      ...(draft.regelkontrolleMode === "upload"
                        ? { background: THEME.primary, borderColor: THEME.primary, color: "#fff" }
                        : { borderColor: THEME.border, color: THEME.text }),
                      borderRadius: "6px",
                      padding: "0.35rem 0.75rem",
                    }}
                  >
                    <CloudUpload size={14} style={{ marginRight: 4 }} />
                    {t("care_chat.upload_document")}
                  </Button>
                </div>

                {draft.regelkontrolleMode === "upload" && (
                  <div className="d-grid gap-2 mt-1">
                    {/* File upload area */}
                    <div
                      className="d-flex flex-column align-items-center justify-content-center gap-2"
                      style={{
                        border: `2px dashed ${
                          draft.regelkontrolleDocument ? THEME.primary : THEME.border
                        }`,
                        borderRadius: "8px",
                        padding: "1.5rem",
                        cursor: "pointer",
                        background: draft.regelkontrolleDocument
                          ? "var(--color-success-subtle, #f0f7f0)"
                          : THEME.background,
                        position: "relative",
                      }}
                      onClick={() => document.getElementById("regelkontrolle-file-input")?.click()}
                    >
                      <input
                        id="regelkontrolle-file-input"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setDraft((prev) => ({
                            ...prev,
                            regelkontrolleDocument: {
                              name: file.name,
                              size: file.size,
                              type: file.type,
                              file,
                            },
                          }));
                          e.target.value = "";
                        }}
                      />
                      {draft.regelkontrolleDocument ? (
                        <>
                          <FileText size={24} style={{ color: THEME.primary }} />
                          <div className="small fw-semibold" style={{ color: THEME.text }}>
                            {draft.regelkontrolleDocument.name}
                          </div>
                          <div className="small" style={{ color: THEME.muted }}>
                            {(draft.regelkontrolleDocument.size / 1024).toFixed(0)} KB
                          </div>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDraft((prev) => ({ ...prev, regelkontrolleDocument: null }));
                            }}
                            style={{
                              borderRadius: "6px",
                              padding: "0.2rem 0.5rem",
                              fontSize: "0.75rem",
                            }}
                          >
                            {t("care_chat.remove")}
                          </Button>
                        </>
                      ) : (
                        <>
                          <CloudUpload size={32} style={{ color: THEME.muted }} />
                          <div className="small" style={{ color: THEME.muted }}>
                            {t("care_chat.upload_document_hint")}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {draft.regelkontrolleMode === "form" && (
                  <>
                    <RegelkontrolleChecklist
                      checks={draft.sichtkontrolleChecks}
                      onToggle={(itemId) =>
                        setDraft((prev) => ({
                          ...prev,
                          sichtkontrolleChecks: {
                            ...(prev.sichtkontrolleChecks || buildChecklistDraft()),
                            [itemId]: !(prev.sichtkontrolleChecks || {})[itemId],
                          },
                        }))
                      }
                    />
                  </>
                )}

                <div className="d-grid gap-2 mt-2">
                  <div className="small fw-semibold" style={{ color: THEME.text }}>
                    {t("care_chat.inspection_data")}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <div className="flex-grow-1">
                      <div className="small" style={{ color: THEME.muted }}>
                        {t("care_chat.inspected_by")}
                      </div>
                      <Form.Control
                        value={draft.kontrolliertVon}
                        onChange={(e) =>
                          setDraft((prev) => ({ ...prev, kontrolliertVon: e.target.value }))
                        }
                        style={INPUT_STYLE}
                      />
                    </div>
                    <div
                      style={{ minWidth: mobile ? 0 : "160px", width: mobile ? "100%" : "auto" }}
                    >
                      <div className="small" style={{ color: THEME.muted }}>
                        {t("care_chat.interval_years")}
                      </div>
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        value={draft.intervallJahre}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            intervallJahre: Number(e.target.value),
                          }))
                        }
                        style={INPUT_STYLE}
                      />
                    </div>
                  </div>
                  <div
                    className="gap-2"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    }}
                  >
                    <div className="d-flex flex-column gap-1">
                      <div className="small" style={{ color: THEME.muted }}>
                        {t("care_chat.date")}
                      </div>
                      <Form.Control
                        type="date"
                        value={
                          draft.kontrolliertAmLocal ? draft.kontrolliertAmLocal.slice(0, 10) : ""
                        }
                        onChange={(e) => {
                          const currentLocal = draft.kontrolliertAmLocal || toLocalDatetimeValue();
                          const nextTimestamp = e.target.value + currentLocal.slice(10);
                          setDraft((prev) => ({ ...prev, kontrolliertAmLocal: nextTimestamp }));
                        }}
                        style={INPUT_STYLE}
                      />
                    </div>
                    <div className="d-flex flex-column gap-1">
                      <div className="small" style={{ color: THEME.muted }}>
                        {t("care_chat.time")}
                      </div>
                      <Form.Control
                        type="time"
                        value={
                          draft.kontrolliertAmLocal ? draft.kontrolliertAmLocal.slice(11, 16) : ""
                        }
                        onChange={(e) => {
                          const currentLocal = draft.kontrolliertAmLocal || toLocalDatetimeValue();
                          const nextTimestamp = currentLocal.slice(0, 11) + e.target.value;
                          setDraft((prev) => ({ ...prev, kontrolliertAmLocal: nextTimestamp }));
                        }}
                        style={INPUT_STYLE}
                      />
                    </div>
                    {!isTaskMode && (
                      <div className="d-flex flex-column gap-1">
                        <div className="small" style={{ color: THEME.muted }}>
                          {t("care_chat.next_inspection")}
                        </div>
                        <Form.Control value={nextControlDisplay} readOnly style={INPUT_STYLE} />
                      </div>
                    )}
                    {/* Temporarily hidden in Aufgaben (tasks) tab per UX request:
                    <div className="d-flex flex-column gap-1">
                      <div className="small" style={{ color: THEME.muted }}>
                        {t("care_chat.next_inspection")}
                      </div>
                      <Form.Control value={nextControlDisplay} readOnly style={INPUT_STYLE} />
                    </div>
                    */}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="small" style={{ color: THEME.muted }}>
                      {t("care_chat.road_safe")}
                    </div>
                    <Form.Check
                      inline
                      type="radio"
                      label={t("care_chat.yes")}
                      name="verkehrssicher"
                      checked={draft.verkehrssicher === true}
                      onChange={() => setDraft((prev) => ({ ...prev, verkehrssicher: true }))}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label={t("care_chat.no")}
                      name="verkehrssicher"
                      checked={draft.verkehrssicher === false}
                      onChange={() => setDraft((prev) => ({ ...prev, verkehrssicher: false }))}
                    />
                  </div>
                </div>

                {draft.regelkontrolleMode === "form" && (
                  <div className="d-grid gap-3 mt-2">
                    <div className="small fw-semibold" style={{ color: THEME.text }}>
                      {t("care_chat.further_action_title")}
                    </div>

                    {/* Handlungsbedarf — nein / ja */}
                    <div
                      className="d-flex align-items-center gap-3"
                      style={{
                        flexDirection: mobile ? "column" : "row",
                        alignItems: mobile ? "flex-start" : "center",
                      }}
                    >
                      <div
                        className="small"
                        style={{ color: THEME.text, minWidth: mobile ? 0 : "140px" }}
                      >
                        {t("care_chat.action_required")}
                      </div>
                      <Form.Check
                        inline
                        type="radio"
                        label={t("care_chat.no")}
                        name="handlungsbedarf"
                        checked={draft.handlungsbedarf === false}
                        onChange={() => setDraft((prev) => ({ ...prev, handlungsbedarf: false }))}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label={t("care_chat.yes")}
                        name="handlungsbedarf"
                        checked={draft.handlungsbedarf === true}
                        onChange={() => setDraft((prev) => ({ ...prev, handlungsbedarf: true }))}
                      />
                    </div>

                    {/* Abstimmung mit Fachabteilung */}
                    <Form.Check
                      type="checkbox"
                      label={t("care_chat.consult_specialist")}
                      checked={draft.abstimmungFach}
                      onChange={() =>
                        setDraft((prev) => ({ ...prev, abstimmungFach: !prev.abstimmungFach }))
                      }
                    />

                    {/* Eingehende Untersuchungen */}
                    <Form.Check
                      type="checkbox"
                      label={t("care_chat.detailed_investigation")}
                      checked={draft.eingehendeUntersuchung}
                      onChange={() =>
                        setDraft((prev) => ({
                          ...prev,
                          eingehendeUntersuchung: !prev.eingehendeUntersuchung,
                        }))
                      }
                    />

                    {/* Baumpflegerische Maßnahmen */}
                    <div className="d-flex flex-column gap-1">
                      <div className="small" style={{ color: THEME.text }}>
                        {t("care_chat.tree_care_measures")}
                      </div>
                      <div className="small" style={{ color: THEME.muted, fontStyle: "italic" }}>
                        {t("care_chat.tree_care_measures_hint")}
                      </div>
                      <Form.Control
                        value={draft.baumpflegerischeMassnahmen}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            baumpflegerischeMassnahmen: e.target.value,
                          }))
                        }
                        style={INPUT_STYLE}
                      />
                    </div>

                    {/* Kontrollintervalle — künftig alle ___ Jahre/jährlich */}
                    <div className="d-flex flex-column gap-2">
                      <div className="small" style={{ color: THEME.text }}>
                        {t("care_chat.inspection_intervals")}
                      </div>
                      <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                        <span
                          className="small"
                          style={{ color: THEME.muted, whiteSpace: mobile ? "normal" : "nowrap" }}
                        >
                          {t("care_chat.interval_future_every")}
                        </span>
                        <Form.Control
                          type="number"
                          min="1"
                          value={draft.kontrollintervallKuenftig}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              kontrollintervallKuenftig: e.target.value,
                            }))
                          }
                          style={{ ...INPUT_STYLE, width: "80px", flexShrink: 0 }}
                        />
                        <span
                          className="small"
                          style={{ color: THEME.muted, whiteSpace: mobile ? "normal" : "nowrap" }}
                        >
                          {t("care_chat.interval_years_annual")}
                        </span>
                      </div>

                      {/* Kontrollintervalle wie bisher */}
                      <Form.Check
                        type="checkbox"
                        label={t("care_chat.interval_as_before")}
                        checked={draft.kontrollintervallWieBisher}
                        onChange={() =>
                          setDraft((prev) => ({
                            ...prev,
                            kontrollintervallWieBisher: !prev.kontrollintervallWieBisher,
                          }))
                        }
                      />
                    </div>

                    {/* Fällung */}
                    <Form.Check
                      type="checkbox"
                      label={t("care_chat.felling")}
                      checked={draft.faellung}
                      onChange={() => setDraft((prev) => ({ ...prev, faellung: !prev.faellung }))}
                    />

                    {/* Erledigung — sofort / innerhalb von ___ Wochen */}
                    <div className="d-flex flex-column gap-2">
                      <div className="small" style={{ color: THEME.text }}>
                        {t("care_chat.completion")}
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <Form.Check
                          type="checkbox"
                          label={t("care_chat.immediately")}
                          checked={draft.erledigungSofort}
                          onChange={() =>
                            setDraft((prev) => ({
                              ...prev,
                              erledigungSofort: !prev.erledigungSofort,
                            }))
                          }
                        />
                      </div>
                      <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                        <span
                          className="small"
                          style={{ color: THEME.muted, whiteSpace: mobile ? "normal" : "nowrap" }}
                        >
                          {t("care_chat.within")}
                        </span>
                        <Form.Control
                          type="number"
                          min="1"
                          value={draft.erledigungWochen}
                          onChange={(e) =>
                            setDraft((prev) => ({ ...prev, erledigungWochen: e.target.value }))
                          }
                          style={{ ...INPUT_STYLE, width: "80px", flexShrink: 0 }}
                        />
                        <span
                          className="small"
                          style={{ color: THEME.muted, whiteSpace: mobile ? "normal" : "nowrap" }}
                        >
                          {t("care_chat.weeks")}
                        </span>
                      </div>
                    </div>

                    {/* Anmerkungen */}
                    <div className="d-flex flex-column gap-1">
                      <div className="small" style={{ color: THEME.text }}>
                        {t("care_chat.remarks_label")}
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={draft.anmerkungen}
                        onChange={(e) =>
                          setDraft((prev) => ({ ...prev, anmerkungen: e.target.value }))
                        }
                        style={INPUT_STYLE}
                      />
                    </div>
                  </div>
                )}
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setStep(2)}
                    style={{
                      borderColor: THEME.primary,
                      color: THEME.primary,
                      borderRadius: "6px",
                      padding: "0.5rem 1rem",
                      backgroundColor: "transparent",
                    }}
                  >
                    {t("care_chat.back")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    style={{
                      background: THEME.primary,
                      borderColor: THEME.primary,
                      borderRadius: "6px",
                      padding: "0.5rem 1rem",
                      color: "#ffffff",
                    }}
                  >
                    {t("care_chat.save")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && isPruning && (
            <div
              className="border rounded-3 p-3"
              style={{ background: THEME.white, borderColor: THEME.border, borderRadius: "8px" }}
            >
              <div className="d-grid gap-2">
                <div className="small fw-semibold" style={{ color: THEME.text }}>
                  {t("care_chat.pruning_protocol_title")}
                </div>
                <div className="d-flex flex-column gap-1">
                  <div className="small" style={{ color: THEME.muted }}>
                    {t("care_chat.pruning_type")} <span style={{ color: THEME.danger }}>*</span>
                  </div>
                  <Form.Select
                    value={draft.pruningType}
                    onChange={(e) => {
                      setDraft((prev) => ({ ...prev, pruningType: e.target.value }));
                      if (showPruningError) setShowPruningError(false);
                    }}
                    style={INPUT_STYLE}
                    isInvalid={showPruningError && !draft.pruningType.trim()}
                  >
                    <option value="">{t("care_chat.select_placeholder")}</option>
                    {PRUNING_CATEGORIES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </option>
                    ))}
                  </Form.Select>
                  {showPruningError && !draft.pruningType.trim() && (
                    <div className="small" style={{ color: THEME.danger }}>
                      {t("care_chat.please_provide_type")}
                    </div>
                  )}
                </div>
                <div className="d-flex flex-column gap-1">
                  <div className="small" style={{ color: THEME.muted }}>
                    {t("care_chat.tree_condition")} <span style={{ color: THEME.danger }}>*</span>
                  </div>
                  <Form.Control
                    value={draft.pruningTreeCondition}
                    onChange={(e) => {
                      setDraft((prev) => ({ ...prev, pruningTreeCondition: e.target.value }));
                      if (showPruningError) setShowPruningError(false);
                    }}
                    placeholder={t("care_chat.tree_condition_placeholder")}
                    style={INPUT_STYLE}
                    isInvalid={showPruningError && !draft.pruningTreeCondition.trim()}
                  />
                  {showPruningError && !draft.pruningTreeCondition.trim() && (
                    <div className="small" style={{ color: THEME.danger }}>
                      {t("care_chat.please_provide_tree_condition")}
                    </div>
                  )}
                </div>
                <div className="d-flex flex-column gap-1">
                  <div className="small" style={{ color: THEME.muted }}>
                    {t("care_chat.remarks")}
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={draft.pruningNotes}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, pruningNotes: e.target.value }))
                    }
                    style={INPUT_STYLE}
                  />
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setStep(2)}
                    style={{
                      borderColor: THEME.primary,
                      color: THEME.primary,
                      borderRadius: "6px",
                      padding: "0.5rem 1rem",
                      backgroundColor: "transparent",
                    }}
                  >
                    {t("care_chat.back")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    style={{
                      background: THEME.primary,
                      borderColor: THEME.primary,
                      borderRadius: "6px",
                      padding: "0.5rem 1rem",
                      color: "#ffffff",
                    }}
                  >
                    {t("care_chat.save")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
